import React from "react";
import { format } from "date-fns";
import { TFunction } from "i18next";
import {
  ICellRendererParams,
  ValueGetterParams,
} from "@ag-grid-community/core";

import { BusinessRole } from "fe-shared/src/types/domain";
import { TransferType, TransferStatus, TransferListItem } from "../types";
import {
  DateRenderer,
  TypeGridCell,
  TransfersNumberRenderer,
  ParticipantRenderer,
  CreatedByRenderer,
  statusGridCellFactory,
  FlagsRenderer,
} from "../components/TransfersDataGrid/renderers";
import AdjustableStatus from "../components/AdjustableStatus";

export const ADJUSTABLE_SUBMITTED_STATUS = "Submitted (Adjustable)";

const ProcessorTransferType = [
  TransferType.IntoProcessing,
  TransferType.OutOfProcessing,
  TransferType.NonEligibleWriteOff,
];

const HaulerTransferType = [
  TransferType.DeliveryToStorage,
  TransferType.PickupFromStorage,
];

const DefaultTransferType = [
  TransferType.IntoProcessing,
  TransferType.OutOfProcessing,
  TransferType.DeliveryToStorage,
  TransferType.PickupFromStorage,
  TransferType.NonEligibleWriteOff,
];

type ConfigMap = {
  [key in BusinessRole]?: TransferType[];
};

const configMap: ConfigMap = {
  [BusinessRole.Processor]: ProcessorTransferType,
  [BusinessRole.Hauler]: HaulerTransferType,
};

export const getFormattedDate = (date?: string) =>
  format(new Date(date || Date.now()), "MMM d, yyyy");

const getTypeFilterOptions = (
  t: TFunction,
  businessRoleCode: BusinessRole | null
) => {
  const currentType = businessRoleCode && configMap[businessRoleCode];

  return Object.values(currentType || DefaultTransferType).map((item) => ({
    value: item,
    label: t(`transfers.type.${item}`),
  }));
};

const getStatusFilterOptions = (t: TFunction) => {
  const statuses = Object.values(TransferStatus).map((item) => ({
    value: item,
    label: t(`transfers.statuses.${item}`),
  }));

  return [
    {
      value: "",
      label: t("transactions.any"),
    },
    {
      value: ADJUSTABLE_SUBMITTED_STATUS,
      label: <AdjustableStatus />,
    },
    ...statuses,
  ];
};

export const getColumnDefs = (
  t: TFunction,
  isInternal: boolean,
  isSelectionAvailable: boolean,
  businessRoleCode: BusinessRole | null = null
) => [
  ...(isSelectionAvailable
    ? [
        {
          headerName: "",
          sortable: false,
          field: "selection",
          width: 30,
          minWidth: 30,
          checkboxSelection: true,
          headerCheckboxSelection: true,
          headerCheckboxSelectionFilteredOnly: false,
        },
        {
          hide: true,
          colId: "isInvoiceable",
          filter: "textFilter",
        },
      ]
    : []),

  {
    headerName: t("transfers.grid.cols.completedDate"),
    field: "completedDate",
    width: 200,
    minWidth: 200,
    cellRenderer: "completedDateRenderer",
    filter: "dateFilter",
    floatingFilterComponent: "dateRangeFilter",
    floatingFilterComponentParams: {
      placeholder: t("transfers.grid.placeholder.completedDate"),
      suppressFilterButton: true,
    },
  },
  {
    headerName: t("transfers.grid.cols.type"),
    field: "transferType",
    width: 240,
    minWidth: 240,
    cellRenderer: "typeRenderer",
    filter: "selectFilter",
    floatingFilterComponent: "multiSelectFilter",
    floatingFilterComponentParams: {
      id: "role-multi-select-filter",
      suppressFilterButton: true,
      options: getTypeFilterOptions(t, businessRoleCode),
    },
    valueGetter: (params: ValueGetterParams) => ({
      transferType: params.data?.transferType,
      isPaperForm: params.data?.isPaperForm,
      isInvoiceable: params.data?.isInvoiceable,
    }),
  },
  {
    headerName: t("transfers.grid.cols.transferNumber"),
    field: "transferNumber",
    cellRenderer: "transferNumberRenderer",
    width: 170,
    minWidth: 170,
    filter: "textFilter",
    filterParams: {
      startFilteringThreshold: 1,
    },
    floatingFilterComponent: "floatingTextFilter",
    floatingFilterComponentParams: {
      placeholder: t("transfers.grid.placeholder.transferNumber"),
      suppressFilterButton: true,
    },
    valueGetter: (params: ValueGetterParams) => ({
      transferNumber: params.data?.transferNumber,
      invoiceNumber: params.data?.invoiceNumber,
    }),
  },
  ...(isInternal
    ? [
        {
          headerName: t("transfers.grid.cols.participant"),
          field: "displayNameOrRegNumber",
          minWidth: 300,
          cellRenderer: "participantRenderer",
          filter: "textFilter",
          flex: 1,
          filterParams: {
            startFilteringThreshold: 1,
          },
          floatingFilterComponent: "floatingTextFilter",
          floatingFilterComponentParams: {
            placeholder: t("transfers.grid.placeholder.participant"),
            suppressFilterButton: true,
          },
        },
      ]
    : []),
  {
    headerName: t("transfers.grid.cols.createdBy"),
    field: "createdBy",
    width: 190,
    minWidth: 190,
    flex: 1,
    cellRenderer: "createByRenderer",
    filter: "textFilter",
    filterParams: {
      startFilteringThreshold: 1,
    },
    floatingFilterComponent: "floatingTextFilter",
    floatingFilterComponentParams: {
      placeholder: t("transfers.grid.placeholder.createBy"),
      suppressFilterButton: true,
    },
  },
  {
    headerName: t("transfers.grid.cols.status"),
    field: "statusCode",
    width: 140,
    minWidth: 140,
    flex: 1,
    cellRenderer: "statusRenderer",
    filter: "singleSelectFilter",
    floatingFilterComponent: "singleSelectComponent",
    floatingFilterComponentParams: {
      id: "role-multi-select-filter",
      suppressFilterButton: true,
      options: getStatusFilterOptions(t),
    },
    valueGetter: (params: ValueGetterParams) => ({
      statusCode: params.data?.statusCode,
      isAdjustable: params.data?.isAdjustable,
    }),
  },
  {
    headerName: "",
    sortable: false,
    field: "flags",
    filter: "textFilter",
    floatingFilterComponent: "clearFilters",
    floatingFilterComponentParams: {
      suppressFilterButton: true,
      id: "transfers-clear-filters-button",
    },
    width: 80,
    minWidth: 80,
    ...(isInternal ? { cellRenderer: "flagsRenderer" } : {}),
  },
];

export const getRenderers = (isInternal: boolean) => ({
  completedDateRenderer: DateRenderer,
  typeRenderer: (props: ICellRendererParams) =>
    TypeGridCell({ ...props, isInternal }),
  transferNumberRenderer: TransfersNumberRenderer,
  participantRenderer: ParticipantRenderer,
  createByRenderer: CreatedByRenderer,
  statusRenderer: statusGridCellFactory<TransferListItem, TransferStatus>(
    (data: TransferListItem) => data.statusCode as TransferStatus
  ),
  flagsRenderer: FlagsRenderer,
});
